import {batch} from 'react-redux';
import {
  A1OffsetCalibrationSubstep,
  PlateDefinitionSubstep,
  SetupWizardStep,
  VisionCalibrationSubstep,
  WizardFlag,
  WizardStatus,
} from '../../../constants/SetupWizardEnum';
import {hideLoading, showLoading} from '../../actions/appStateAction';
import {
  clearWizardFlag,
  decrementCurrentWizardView,
  incrementCurrentWizardView,
  incrementProcessedStep,
  setCurrentStepData,
  setCurrentWizardView,
  setStepData,
  setStepName,
  setWizardAllowedNext,
  setWizardStatus,
} from '../../actions/wizardStateAction';
import {saveState} from '../stateBusiness';
// eslint-disable-next-line import/no-cycle
import {
  CommonSetupWizardBusiness,
  getSetupWizardCurrentStepData,
  getSetupWizardNextStepData,
  getSetupWizardPreviousStepData,
  incrementSetupWizardSubstep,
  initializeDefaultStepData,
  isOnLastSubstep,
} from './commonSetupWizardBusiness';

// #region navigation control
export const setButtonContinueAccessForward =
  (skipStep = false) =>
  (dispatch, getState) => {
    const {wizardState} = getState();
    const {stepName} = wizardState;

    const currentStepData = dispatch(getSetupWizardCurrentStepData()) || {};
    const {currentSubstep, isStepProcessed} = currentStepData;

    const nextStepData = dispatch(getSetupWizardNextStepData(true));
    const {isStepProcessed: isNextStepProcessed, stepName: nextStepName} = nextStepData;

    const isLastSubstep = dispatch(isOnLastSubstep());

    let isNextAllowed = false;

    // move next substep in the same step
    if (
      currentSubstep &&
      !isLastSubstep &&
      (!skipStep || (skipStep && stepName === SetupWizardStep.VISION_CALIBRATION))
    ) {
      isNextAllowed = isStepProcessed ?? false;
      if (stepName === SetupWizardStep.VISION_CALIBRATION) {
        const subStepProcessed = currentStepData?.subStepProcessed ?? [];
        const {isSlantedDetectionSkipped} = currentStepData;
        let nextSubstep = currentSubstep + 1;

        if (currentSubstep === VisionCalibrationSubstep.SCAN_TIPS && isSlantedDetectionSkipped) {
          nextSubstep = currentSubstep + 2;
        }

        isNextAllowed = subStepProcessed.includes(nextSubstep);
      }
    } else {
      // move to next step
      isNextAllowed = isNextStepProcessed ?? false;

      // line 44-52:
      // these are the substeps/steps that have no requirement to continue so the continue button will always be enabled
      if (nextStepName === SetupWizardStep.A1_OFFSET_CALIBRATION) {
        isNextAllowed = true;
      }

      if (nextStepName === SetupWizardStep.PLATE_COPY_PROTOCOL || nextStepName === SetupWizardStep.REPORT) {
        isNextAllowed = true;
      }

      // plate definition continue button are disabled by default because the touch probe needs to be extended first
      if (nextStepName === SetupWizardStep.PLATE_DEFINITION) {
        isNextAllowed = false;
      }

      if (nextStepName === SetupWizardStep.VISION_CALIBRATION) {
        const subStepProcessed = nextStepData?.subStepProcessed ?? [];
        isNextAllowed = subStepProcessed.includes(VisionCalibrationSubstep.SCAN_TIPS);
      }
    }

    dispatch(setWizardAllowedNext(isNextAllowed));
  };

export const setButtonContinueAccessBackward = () => (dispatch, getState) => {
  const {wizardState} = getState();
  const {currentWizardView, wizardFlag, wizardViewData, stepName} = wizardState;

  const {currentSubstep, isSlantedDetectionSkipped, isStepProcessed} = dispatch(getSetupWizardCurrentStepData()) || {};

  const prevWizardView = currentWizardView - 1;
  const isPrevStepProcessed = wizardViewData[prevWizardView]?.isStepProcessed ?? false;
  const prevStepName = wizardViewData[prevWizardView].stepName;

  let isNextAllowed = currentSubstep && currentSubstep > 1 ? isStepProcessed : isPrevStepProcessed;

  // back from substep in same step
  if (currentSubstep && currentSubstep > 1) {
    if (stepName === SetupWizardStep.PLATE_DEFINITION && currentSubstep !== PlateDefinitionSubstep.GUIDE_IMAGE) {
      isNextAllowed = false;
    }

    if (
      (stepName === SetupWizardStep.PLATE_COPY_PROTOCOL && currentSubstep !== PlateDefinitionSubstep.GUIDE_IMAGE) ||
      (stepName === SetupWizardStep.A1_OFFSET_CALIBRATION && currentSubstep !== A1OffsetCalibrationSubstep.GUIDE_IMAGE)
    ) {
      isNextAllowed = true;
    }

    if (stepName === SetupWizardStep.VISION_CALIBRATION && currentSubstep !== VisionCalibrationSubstep.SCAN_TIPS) {
      const subStepProcessed = wizardViewData[currentWizardView]?.subStepProcessed ?? [];
      let prevCurrentSubstep = currentSubstep - 1;
      if (currentSubstep === VisionCalibrationSubstep.STUCK_TIPS_GUIDE && isSlantedDetectionSkipped) {
        prevCurrentSubstep = currentSubstep - 2;
      }

      isNextAllowed = subStepProcessed.includes(prevCurrentSubstep);
    }
  } else {
    // back from another step
    if (
      prevStepName === SetupWizardStep.A1_OFFSET_CALIBRATION ||
      prevStepName === SetupWizardStep.PLATE_COPY_PROTOCOL
    ) {
      isNextAllowed = true;
    }

    if (prevStepName === SetupWizardStep.VISION_CALIBRATION) {
      const prevSubStepProcessed = wizardViewData[currentWizardView - 1]?.subStepProcessed ?? [];
      isNextAllowed = prevSubStepProcessed.includes(VisionCalibrationSubstep.SCAN_TIPS);
    }
  }

  if (wizardFlag === WizardFlag.ERROR) {
    isNextAllowed = false;
  }

  dispatch(setWizardAllowedNext(isNextAllowed));
};

export const setSkippableNextStep = (shouldIncrementStep) => (dispatch) => {
  if (!shouldIncrementStep) {
    const currentStepData = dispatch(getSetupWizardCurrentStepData()) || {};
    const {step, currentSubstep, isStepProcessed, stepName} = currentStepData;

    if (stepName === SetupWizardStep.VISION_CALIBRATION) {
      const subStepProcessed = currentStepData?.subStepProcessed ?? [];

      dispatch(setStepData(step, {isSkippable: !subStepProcessed.includes(currentSubstep)}));
    }

    if (
      stepName === SetupWizardStep.A1_OFFSET_CALIBRATION ||
      stepName === SetupWizardStep.PLATE_DEFINITION ||
      stepName === SetupWizardStep.PLATE_COPY_PROTOCOL
    ) {
      dispatch(setStepData(step, {isSkippable: currentSubstep === 1 ? true : !isStepProcessed}));
    }
    return;
  }

  const nextStepData = dispatch(getSetupWizardNextStepData());
  const {step: nextStepNumber, stepName: nextStepName, currentSubstep, isStepProcessed} = nextStepData || {};

  if (
    nextStepName === SetupWizardStep.A1_OFFSET_CALIBRATION ||
    nextStepName === SetupWizardStep.PLATE_DEFINITION ||
    nextStepName === SetupWizardStep.PLATE_COPY_PROTOCOL
  ) {
    dispatch(setStepData(nextStepNumber, {isSkippable: currentSubstep === 1 ? true : !isStepProcessed}));
  }
};

export const setSkippablePrevStep = (shouldDecrementStep) => (dispatch) => {
  if (!shouldDecrementStep) {
    const {step, stepName, currentSubstep, subStepProcessed, isSlantedDetectionSkipped} =
      dispatch(getSetupWizardCurrentStepData()) || {};

    if (stepName === SetupWizardStep.VISION_CALIBRATION) {
      let prevSubstep = currentSubstep - 1;
      if (currentSubstep === VisionCalibrationSubstep.STUCK_TIPS_GUIDE && isSlantedDetectionSkipped) {
        prevSubstep = currentSubstep - 2;
      }
      dispatch(
        setStepData(step, {
          isSkippable: !subStepProcessed?.includes(prevSubstep),
        }),
      );
    } else if (
      currentSubstep === 1 &&
      (stepName === SetupWizardStep.A1_OFFSET_CALIBRATION ||
        stepName === SetupWizardStep.PLATE_DEFINITION ||
        stepName === SetupWizardStep.PLATE_COPY_PROTOCOL)
    ) {
      dispatch(setStepData(step, {isSkippable: true}));
    }

    return;
  }

  const {
    step: prevStep,
    stepName: prevStepName,
    currentSubstep: prevSubstep,
    subStepProcessed: prevSubStepProcessed,
  } = dispatch(getSetupWizardPreviousStepData()) || {};

  if (
    prevSubstep === 1 &&
    (prevStepName === SetupWizardStep.A1_OFFSET_CALIBRATION ||
      prevStepName === SetupWizardStep.PLATE_DEFINITION ||
      prevStepName === SetupWizardStep.PLATE_COPY_PROTOCOL)
  ) {
    dispatch(setStepData(prevStep, {isSkippable: true}));
  }

  if (prevStepName === SetupWizardStep.VISION_CALIBRATION) {
    dispatch(
      setStepData(prevStep, {
        isSkippable: !prevSubStepProcessed?.includes(VisionCalibrationSubstep.SCAN_TIPS),
      }),
    );
  }
};

const processSubstepIncrement = (skipSubstep) => async (dispatch) => {
  const {currentSubstep, processedSubstep} = dispatch(getSetupWizardCurrentStepData()) || {};
  let updatedProcessedSubstep = processedSubstep;
  const {stepName: nextStep} = dispatch(getSetupWizardNextStepData());

  if (currentSubstep) {
    const isLastSubstep = dispatch(isOnLastSubstep());

    if (!skipSubstep && currentSubstep === processedSubstep + 1) {
      updatedProcessedSubstep += 1;
      dispatch(setCurrentStepData({processedSubstep: updatedProcessedSubstep}));
    }

    if (!isLastSubstep) {
      if (!skipSubstep) dispatch(incrementSetupWizardSubstep());
      await dispatch(saveState());
      if (!skipSubstep) return false;
    }

    if (nextStep === SetupWizardStep.REPORT) {
      dispatch(setWizardStatus(WizardStatus.COMPLETED));
    }
  }

  if (nextStep === SetupWizardStep.FINISH_SCREEN) {
    dispatch(setWizardStatus(WizardStatus.NOT_STARTED));
  }
  return true;
};

export const handleIncrementCurrentWizardView =
  (skipSubstep = false, checkContinueBtnAccess = false) =>
  async (dispatch, getState) => {
    const {wizardState} = getState();
    const {processedStep, currentWizardView, stepName} = wizardState;
    const {step: previousStepData} = dispatch(getSetupWizardPreviousStepData()) || {};
    const nextStepData = dispatch(getSetupWizardNextStepData()) || {};

    let isSkipSubstep = skipSubstep;

    if (stepName === SetupWizardStep.VISION_CALIBRATION) {
      isSkipSubstep = false;
    }

    if (stepName !== SetupWizardStep.REPORT) {
      dispatch(clearWizardFlag());
    }

    dispatch(showLoading());

    if (checkContinueBtnAccess) await dispatch(setButtonContinueAccessForward(skipSubstep));

    const shouldIncrementStep = await dispatch(processSubstepIncrement(isSkipSubstep));

    dispatch(setSkippableNextStep(shouldIncrementStep));

    if (!shouldIncrementStep) {
      dispatch(hideLoading());
      return;
    }

    if (previousStepData === processedStep) dispatch(incrementProcessedStep());

    if (nextStepData.step) dispatch(initializeDefaultStepData(currentWizardView + 1, true));

    batch(() => {
      dispatch(hideLoading());
      dispatch(incrementCurrentWizardView());
      dispatch(setStepName(nextStepData.stepName));
    });

    await dispatch(saveState());
  };

const processSubstepDecrement = () => async (dispatch) => {
  const {currentSubstep, step, stepName} = dispatch(getSetupWizardCurrentStepData()) || {};
  const {step: prevStep, currentSubstep: prevSubStep} = dispatch(getSetupWizardPreviousStepData()) || {};

  if (currentSubstep > 1) {
    let newSubstep = currentSubstep - 1;

    // on plate definition step, well measurement substep will be skipped
    // if accesed by using back button
    if (stepName === SetupWizardStep.PLATE_DEFINITION && currentSubstep === PlateDefinitionSubstep.SUMMARY) {
      newSubstep = PlateDefinitionSubstep.GUIDE_IMAGE;
    }

    dispatch(setCurrentStepData({currentSubstep: newSubstep}));
    await dispatch(saveState());
    return false;
  }

  // always go back to first sub step from another step
  if (prevSubStep && step !== prevStep) {
    dispatch(setStepData(prevStep, {currentSubstep: 1}));
  }

  return true;
};

export const handleDecrementCurrentWizardView =
  (checkContinueBtnAccess = true) =>
  async (dispatch, getState) => {
    const {wizardState} = getState();
    const {wizardFlag} = wizardState;

    if (wizardFlag !== WizardFlag.NONE) {
      dispatch(clearWizardFlag());
      return;
    }

    dispatch(showLoading());

    const previousStepData = dispatch(getSetupWizardPreviousStepData());

    if (previousStepData.stepName === SetupWizardStep.WELCOME_SCREEN) {
      dispatch(setWizardStatus(WizardStatus.NOT_STARTED));
    }

    if (previousStepData.stepName === SetupWizardStep.PLATE_COPY_PROTOCOL) {
      dispatch(setWizardStatus(WizardStatus.ONGOING));
    }

    if (checkContinueBtnAccess) await dispatch(setButtonContinueAccessBackward());

    const shouldDecrementStep = await dispatch(processSubstepDecrement());
    dispatch(setSkippablePrevStep(shouldDecrementStep));

    if (!shouldDecrementStep) {
      dispatch(hideLoading());
      return;
    }

    batch(() => {
      dispatch(hideLoading());
      dispatch(decrementCurrentWizardView());
      dispatch(setStepName(previousStepData.stepName));
    });

    await dispatch(saveState());
  };

// #endregion navigation control

export const navigateSetupWizardToStep = (stepName) => (dispatch) => {
  const stepNumber = CommonSetupWizardBusiness.getStepNumberByName(stepName);

  batch(() => {
    dispatch(setCurrentWizardView(stepNumber));
    dispatch(setStepName(stepName));
  });
};
